/**
 * `useDataTableGridNav` — wires the `useGridKeyboardNav` engine to the preset so its
 * `role="grid"` is keyboard-operable: arrows/Home/End move the active cell via
 * `aria-activedescendant` (not roving `tabIndex`, so a windowed-out row never drops focus
 * to `<body>`), Space toggles row selection, Enter activates the row.
 *
 * Preset seam: a hand-rendered selection column occupies visible-column 0 (outside the
 * TanStack model), so `colCount`/`getCellId` shift data columns +1 to match the rendered
 * `aria-colindex` graph.
 */
import {
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useId,
} from 'react'
import { useEvent } from 'react-use-event-hook'
import type { Row, RowData, Table } from '@tanstack/react-table'
import type { Virtualizer } from '@tanstack/react-virtual'

import {
  buildGridCellId,
  type GridCoord,
  useGridKeyboardNav,
} from './primitives/use-grid-keyboard-nav'

export type UseDataTableGridNavOptions<TData extends RowData> = {
  table: Table<TData>
  /**
   * The RAW engine rows, indexed by SERVER OFFSET (may contain duplicates). The nav's
   * `rowIndex` is a LOGICAL coordinate, so translate it through `logicalToVirtual` before
   * indexing `rows` (identity in dense, the kept server offset in sparse) — a bare `rows[rowIndex]`
   * selects the wrong record past a dropped duplicate.
   */
  rows: Row<TData>[]
  virtualizer: Virtualizer<HTMLElement, Element>
  isWindowed: boolean
  /** Loaded (logical, deduped) row count. */
  logicalRowCount: number
  /**
   * Logical row index → virtualizer slot: identity in the dense model, the row's server
   * offset in the sparse model. Keeps scroll-into-view on the right slot regardless of
   * count model.
   */
  logicalToVirtual: (logicalIndex: number) => number
  /**
   * Virtualizer slot → logical row (`-1` for an unloaded sparse placeholder): identity in
   * dense, the server-offset reconciliation in sparse. Translates the mounted slot window
   * into the logical range keyboard re-anchor is expressed in.
   */
  virtualToLogical: (virtualIndex: number) => number
  hasSelectionColumn: boolean
  /** Omitted (no `selection` model) → Space is captured (no page-scroll) but a no-op. */
  onToggleRowSelection?: (rowId: string) => void
}

export type DataTableGridNav = {
  activeDescendantId: string | undefined
  /** Acts only on the container's own key events (see the guard in the hook body). */
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void
  /** Anchors the active cell on first entry; container-only. */
  onFocus: (event: ReactFocusEvent<HTMLElement>) => void
  getCellId: (coord: GridCoord) => string
  isActive: (coord: GridCoord) => boolean
  colCount: number
  /**
   * The per-instance id namespace, so a body row can derive its cell ids through the pure
   * `buildGridCellId` from PRIMITIVE props (`gridId` + `activeCellId`) instead of holding
   * this whole object — fresh functions here would defeat the rows' memo bailout.
   */
  gridId: string
}

/**
 * Translate a mounted virtual-SLOT window into the LOGICAL row range `[start, end)` that
 * keyboard re-anchor is expressed in. Slots are server offsets in the sparse model, so each is
 * mapped through `virtualToLogical`; dedup can make the mapping non-monotonic near a dropped
 * duplicate, so every slot is scanned (not just the ends) and unloaded (`-1`) placeholder slots
 * are skipped. An all-placeholder window yields an empty range → the caller re-anchors to row 0.
 * Pure + exported so the subtle translation can be unit-tested without a virtualizer or scroll.
 */
export const deriveMountedLogicalRange = (
  items: readonly { index: number }[],
  virtualToLogical: (virtualIndex: number) => number,
): { start: number; end: number } => {
  let start = Number.POSITIVE_INFINITY
  let end = 0
  for (const item of items) {
    const logical = virtualToLogical(item.index)
    if (logical < 0) {
      continue
    }
    if (logical < start) {
      start = logical
    }
    if (logical + 1 > end) {
      end = logical + 1
    }
  }

  return start < end ? { start, end } : { start: 0, end: 0 }
}

export const useDataTableGridNav = <TData extends RowData>(
  options: UseDataTableGridNavOptions<TData>,
): DataTableGridNav => {
  const {
    table,
    rows,
    virtualizer,
    isWindowed,
    logicalRowCount,
    logicalToVirtual,
    virtualToLogical,
    hasSelectionColumn,
    onToggleRowSelection,
  } = options

  // Per-instance id namespace so cell ids stay unique across multiple grids on a page and
  // `aria-activedescendant` resolves to THIS grid's cell.
  const gridId = useId()
  const getCellId = (coord: GridCoord): string => buildGridCellId(gridId, coord)

  // Data columns + the model-external selection column, so the coordinate space matches
  // the rendered `aria-colindex` graph. `getVisibleLeafColumns()` excludes hidden columns.
  const colCount = table.getVisibleLeafColumns().length + (hasSelectionColumn ? 1 : 0)

  const nav = useGridKeyboardNav({
    rowCount: logicalRowCount,
    colCount,
    getCellId,
    // Instant scroll (never smooth) so held-arrow nav isn't motion-triggering. A non-windowed
    // client tier mounts every row (nothing to scroll into view); the windowed path — infinite
    // OR a client table with `virtualizeRows` — scrolls the active row's slot into the window.
    scrollToLogicalIndex: (logicalIndex) => {
      if (isWindowed) {
        // Scroll to the row's slot: identity in dense, its server offset in sparse.
        virtualizer.scrollToIndex(logicalToVirtual(logicalIndex), {
          align: 'auto',
          behavior: 'auto',
        })
      }
    },
    getMountedLogicalRange: () => {
      // Not windowed (client tier) or nothing mounted yet → the whole logical set is treated as
      // mounted. `logicalRowCount` (not `rows.length`) keeps this in the deduped LOGICAL space the
      // range is documented in — `rows` is the raw engine model and can be longer under dedup.
      const items = isWindowed ? virtualizer.getVirtualItems() : []
      if (items.length === 0) {
        return { start: 0, end: logicalRowCount }
      }

      return deriveMountedLogicalRange(items, virtualToLogical)
    },
    onToggleSelect: ({ rowIndex }) => {
      // `rowIndex` is a LOGICAL coordinate (nav space is `logicalRowCount`), but `rows` is the
      // raw engine model indexed by server offset — the same divergence the windowed render
      // reconciles. Translate through `logicalToVirtual` (== the kept server offset in sparse,
      // identity in dense) so Space toggles the row actually shown at that coordinate, not the
      // raw row at the deduped index (which would select the wrong record past a duplicate).
      const row = rows[logicalToVirtual(rowIndex)]
      if (row !== undefined) {
        onToggleRowSelection?.(row.id)
      }
    },
    // A real DOM click on the row (not a synthetic) so the row's own `onClick` fires and
    // the selection cell's `stopPropagation` is respected.
    onActivate: (coord) => {
      const cell = document.getElementById(getCellId(coord))
      cell?.closest<HTMLElement>('[role="row"]')?.click()
    },
  })

  // Act ONLY when the grid container is the event target, never on events bubbling from
  // cell content: Base UI `role="checkbox"` buttons don't stop keydown propagation, so
  // without this guard a Space/Enter on a focused checkbox would mis-fire on the active
  // cell and focusing a checkbox would re-anchor it to {0,0}.
  const onKeyDown = useEvent((event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) {
      nav.onKeyDown(event)
    }
  })
  const onFocus = useEvent((event: ReactFocusEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) {
      nav.onFocus()
    }
  })

  return {
    activeDescendantId: nav.activeDescendantId,
    onKeyDown,
    onFocus,
    getCellId,
    isActive: nav.isActive,
    colCount,
    gridId,
  }
}
